export interface RpaBootstrap {
  restBase: string
  nonce: string
  pluginUrl: string
  loginUrl?: string
  logoutUrl?: string
  page?: string
  isLoggedIn?: boolean
  unread_total?: number
  defaultStationId?: number
  prepDashboardOptimized?: boolean
}

declare global {
  interface Window {
    __RPA_BOOTSTRAP__?: RpaBootstrap
  }
}

export function getBootstrap(): RpaBootstrap {
  const bootstrap = window.__RPA_BOOTSTRAP__
  if (!bootstrap) {
    throw new Error('RPA bootstrap globals missing.')
  }
  return bootstrap
}

export interface RequestOptions {
  signal?: AbortSignal
  cache?: RequestCache
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options: RequestOptions = {},
): Promise<T> {
  const { restBase, nonce } = getBootstrap()
  const url = new URL(path, restBase)

  const headers = new Headers({
    'X-WP-Nonce': nonce,
  })

  if (body !== undefined && method !== 'GET') {
    headers.set('Content-Type', 'application/json')
  }

  const response = await fetch(url.toString(), {
    method,
    credentials: 'same-origin',
    headers,
    body: body !== undefined && method !== 'GET' ? JSON.stringify(body) : undefined,
    signal: options.signal,
    cache: options.cache,
  })

  const text = await response.text()
  const hasBody = text.length > 0
  let data: unknown = null
  const contentType = response.headers.get('Content-Type') ?? ''

  if (hasBody) {
    const isJson = contentType.toLowerCase().includes('application/json')

    if (isJson) {
      try {
        data = JSON.parse(text)
      } catch (error) {
        if (response.ok) {
          throw new Error('Failed to parse response from server.')
        }
      }
    } else if (response.ok) {
      throw new Error('Unexpected response format from server.')
    }
  }

  if (!response.ok) {
    const message =
      data && typeof data === 'object' && 'message' in data
        ? String((data as { message?: unknown }).message ?? 'Request failed')
        : `Request failed with status ${response.status}`
    const error = new Error(message) as Error & { status?: number; data?: unknown }
    error.status = response.status
    error.data = data
    throw error
  }

  return data as T
}

export async function apiGet<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return request<T>('GET', path, undefined, options)
}

export async function apiPost<T>(
  path: string,
  body?: unknown,
  options: RequestOptions = {},
): Promise<T> {
  return request<T>('POST', path, body, options)
}

export async function apiPut<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
  return request<T>('PUT', path, body, options)
}

export async function apiDelete<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return request<T>('DELETE', path, undefined, options)
}
